import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import { SelectComponent } from "@/components/select/Select";
import { AppColors } from "@/styles/colors";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Filter, SearchIcon } from "lucide-react";
import { Dispatch, SetStateAction } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  disableApply?: boolean;
}

export const ProductionGrainsFilter = ({
  handleSubmit,
  openFilters,
  setOpenFilters,
  submitFilters,
  clearFilters,
  control,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  disableApply,
}: Props) => {
  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter size={16} />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear || disableFilters}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <InputField
                    inputName="Nombre"
                    labelText="Nombre"
                    placeholder="Ingresar nombre"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem>
                  <InputField
                    inputName="GranosAdmis"
                    labelText="Código ADMIS"
                    placeholder="Ingresar código"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="AplicaPPMB"
                    items={[
                      { label: "Sí", value: true },
                      { label: "No", value: false },
                    ]}
                    labelText="Aplica PP y MB"
                    placeholder="Seleccionar"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                    menuMaxHeight="250px"
                    minHeight="42px"
                  />
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="SumaHA"
                    items={[
                      { label: "Sí", value: true },
                      { label: "No", value: false },
                    ]}
                    labelText="Suma HA"
                    placeholder="Seleccionar"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                    menuMaxHeight="250px"
                    minHeight="42px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters || disableApply}
                data-testid="production-grains-apply-filters-btn"
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
